import { Button, Result, Spin } from 'antd';
import React, { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { AuthBackground } from '@/components/common';
import { ROUTES } from '@/constants';
import { useCurrentUser } from '@/hooks/auth';
import type { UserRole } from '@/types';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
	children: React.ReactNode;
	requiredRole?: UserRole;
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
	const { t } = useTranslation('components');
	const location = useLocation();
	const navigate = useNavigate();
	const { data: user, isLoading, error } = useCurrentUser();

	const handleGoToDashboard = useCallback(() => {
		navigate(ROUTES.DASHBOARD);
	}, [navigate]);

	const handleLogout = useCallback(() => {
		// Clear user data and redirect to login
		navigate(ROUTES.LOGIN, { state: { from: location }, replace: true });
	}, [navigate, location]);

	// Show loading while checking authentication
	if (isLoading) {
		return (
			<div
				className='min-h-screen flex items-center justify-center p-4 w-full bg-cover bg-center bg-no-repeat'
				style={{ backgroundImage: 'url(/auth-bg.svg)' }}
			>
				<div className='text-center bg-white/50 backdrop-blur-sm p-8 rounded-lg'>
					<Spin size='default' className='mb-4' />
					<div className='text-black text-base'>{t('auth.validatingAuth')}</div>
				</div>
			</div>
		);
	}

	// Redirect unauthenticated users to login
	if (error || !user) {
		return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
	}

	// Check role requirements if specified
	if (requiredRole && user.role !== requiredRole) {
		return (
			<AuthBackground variant='minimal'>
				<div className='bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-100'>
					<Result
						status='403'
						title={<span className='text-2xl font-bold text-gray-900'>{t('auth.accessDenied')}</span>}
						subTitle={<span className='text-gray-500'>{t('auth.noPermission')}</span>}
						extra={
							<div className='flex flex-col gap-3 mt-4'>
								<Button type='primary' size='large' block onClick={handleGoToDashboard} className='bg-indigo-600 hover:bg-indigo-700 h-10 rounded-lg'>
									{t('auth.goToDashboard')}
								</Button>
								<Button size='large' block onClick={handleLogout} className='h-10 rounded-lg border-gray-300 hover:border-gray-400 text-gray-700'>
									{t('header.logout')}
								</Button>
							</div>
						}
					/>
				</div>
			</AuthBackground>
		);
	}

	// User is authenticated and has required role, render children
	return <>{children}</>;
};

export { ProtectedRoute };
export default ProtectedRoute;
